<script setup lang="ts" generic="T extends string | number | boolean | object | null | undefined">
	import type { PaginationContext } from '@/types'
	import { onClickOutside, useDebounceFn } from '@vueuse/core'
	import { Form } from 'ant-design-vue'
	import type { ClassNameValue } from 'tailwind-merge'

	export interface SelectOption<V = unknown> {
		label?: unknown
		value: V
		disabled?: boolean
		hidden?: boolean
		class?: ClassNameValue
		title?: string
		[key: string]: unknown
	}

	interface PaginationMeta {
		current_page: number
		per_page: number
		total_pages: number
		total: number
	}

	const props = withDefaults(defineProps<UiSelectProps<T>>(), {
		placeholder: 'Select option',
		showArrow: true
	})

	const modelValue = defineModel<T>()
	const isOpen = shallowRef(false)
	const isAbove = shallowRef(false)
	const searchQuery = ref('')
	const internalOptions = shallowRef<SelectOption<T>[]>([])
	const isLoading = ref(false)
	const isLoadingMore = ref(false)
	const paginationMeta = ref<PaginationMeta>({
		current_page: 0,
		per_page: 30,
		total_pages: 0,
		total: 0
	})

	const target = useTemplateRef<HTMLElement>('target')
	const menuRef = useTemplateRef<HTMLElement>('menu')
	const emit = defineEmits<{ (e: 'change', value: T, option?: SelectOption<unknown>): void }>()
	const formItemContext = Form.useInjectFormItemContext()

	const triggerRect = ref({ top: 0, left: 0, width: 0, height: 0 })

	const hasFetchFn = computed(() => typeof props.options === 'function' || !!props.optionsFn)

	const updatePosition = () => {
		if (!target.value) return
		const rect = target.value.getBoundingClientRect()
		triggerRect.value = {
			top: rect.top,
			left: rect.left,
			width: rect.width,
			height: rect.height
		}
		const spaceBelow = window.innerHeight - rect.bottom
		isAbove.value = spaceBelow < 260 && rect.top > spaceBelow
	}

	const menuStyle = computed(() => {
		if (!triggerRect.value) return {}
		return {
			position: 'fixed' as const,
			top: isAbove.value ? `${triggerRect.value.top}px` : `${triggerRect.value.top + triggerRect.value.height}px`,
			left: `${triggerRect.value.left}px`,
			width: `${triggerRect.value.width}px`,
			transform: isAbove.value ? 'translateY(-100%) translateY(-8px)' : 'translateY(8px)',
			zIndex: 1100
		}
	})

	const toggle = () => {
		if (props.disabled || props.loading) return
		if (!isOpen.value) {
			updatePosition()
			searchQuery.value = ''
			if (hasFetchFn.value && internalOptions.value.length === 0) {
				fetchOptions(true)
			}
			window.addEventListener('scroll', updatePosition, true)
			window.addEventListener('resize', updatePosition)
		} else {
			window.removeEventListener('scroll', updatePosition, true)
			window.removeEventListener('resize', updatePosition)
		}
		isOpen.value = !isOpen.value
	}

	const close = () => {
		isOpen.value = false
		searchQuery.value = ''
		window.removeEventListener('scroll', updatePosition, true)
		window.removeEventListener('resize', updatePosition)
	}

	const onClear = () => {
		modelValue.value = undefined as T
		emit('change', undefined as unknown as T, undefined)
		formItemContext.onFieldChange()
	}

	const selectOption = (option: SelectOption<T>) => {
		if (option.disabled) return
		modelValue.value = option.value
		emit('change', option.value, option)
		formItemContext.onFieldChange()
		close()
	}

	const fetchOptions = async (reset = false) => {
		if (props.disabled) return
		const fetchFn = typeof props.options === 'function' ? props.options : props.optionsFn
		if (!fetchFn || isLoading.value || isLoadingMore.value) return

		if (reset) {
			isLoading.value = true
			paginationMeta.value.current_page = 1
		} else {
			if (paginationMeta.value.current_page >= paginationMeta.value.total_pages) return
			isLoadingMore.value = true
			paginationMeta.value.current_page++
		}

		try {
			const response = await fetchFn({
				page: paginationMeta.value.current_page,
				search: searchQuery.value,
				query: searchQuery.value
			})

			if (response) {
				let newOptions = response.items || []
				if (props.labelKey && props.valueKey) {
					newOptions = (newOptions as Record<string, unknown>[]).map(item => {
						const label =
							typeof props.labelKey === 'function' ? props.labelKey(item) : item[props.labelKey as string]
						const value =
							typeof props.valueKey === 'function' ? props.valueKey(item) : item[props.valueKey as string]
						return {
							...item,
							label,
							value
						}
					})
				}

				if (reset) {
					internalOptions.value = newOptions as SelectOption<T>[]
				} else {
					// Prevent duplicates
					const existingValues = new Set(internalOptions.value.map(opt => String(opt.value)))
					const uniqueNewOptions = (newOptions as SelectOption<T>[]).filter(
						opt => !existingValues.has(String(opt.value))
					)
					internalOptions.value.push(...uniqueNewOptions)
				}
				paginationMeta.value = {
					current_page:
						Number(response.current_page || (response as unknown as Record<string, unknown>).currentPage) ||
						1,
					per_page: Number(response.per_page || response.limit) || 30,
					total_pages:
						Number(response.total_pages || (response as unknown as Record<string, unknown>).totalPages) ||
						1,
					total: Number(response.total || response.total_count) || 0
				}
			}
		} catch (error) {
			console.error('Failed to fetch options:', error)
		} finally {
			isLoading.value = false
			isLoadingMore.value = false
		}
	}

	const debouncedFetch = useDebounceFn(() => fetchOptions(true), 300)

	watch(searchQuery, () => {
		// Only hit the API when we are NOT doing client-side search.
		// If useClientSearch is true the filtering happens entirely in filteredOptions.
		if (hasFetchFn.value && !props.useClientSearch) {
			debouncedFetch()
		}
	})

	const handleScroll = (e: Event) => {
		if (!hasFetchFn.value || isLoading.value || isLoadingMore.value) return
		const target = e.target as HTMLElement
		const threshold = 50
		if (target.scrollTop + target.clientHeight >= target.scrollHeight - threshold) {
			fetchOptions(false)
		}
	}

	/**
	 * Computes a Levenshtein distance between two strings (lower = more similar).
	 * Used only as a tie-breaker inside the ranked client search.
	 */
	function levenshtein(a: string, b: string): number {
		const dp: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
			Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
		)
		for (let i = 1; i <= a.length; i++) {
			for (let j = 1; j <= b.length; j++) {
				dp[i]![j] =
					a[i - 1] === b[j - 1]
						? dp[i - 1]![j - 1]!
						: 1 + Math.min(dp[i - 1]![j]!, dp[i]![j - 1]!, dp[i - 1]![j - 1]!)
			}
		}
		return dp[a.length]![b.length]!
	}

	/**
	 * Ranks a list of options against a search query.
	 *
	 * Priority order:
	 *   0 – exact match (label === query)
	 *   1 – label starts with query
	 *   2 – label contains query
	 *   3 – remainder, sorted ascending by Levenshtein distance so closest
	 *       spelling variants appear before alphabetically later options.
	 */
	function rankOptions(options: SelectOption<T>[], query: string): SelectOption<T>[] {
		const q = query.toLowerCase().trim()
		if (!q) return options

		const buckets: [SelectOption<T>[], SelectOption<T>[], SelectOption<T>[], SelectOption<T>[]] = [
			/* exact */ [],
			/* starts-with */ [],
			/* contains */ [],
			/* closest spelling */ []
		]

		for (const opt of options) {
			const haystack = String(opt.label ?? opt.value).toLowerCase()
			if (haystack === q) {
				buckets[0].push(opt)
			} else if (haystack.startsWith(q)) {
				buckets[1].push(opt)
			} else if (haystack.includes(q)) {
				buckets[2].push(opt)
			} else {
				buckets[3].push(opt)
			}
		}

		// Sort the "rest" bucket by Levenshtein distance ascending
		buckets[3].sort((a, b) => {
			const ha = String(a.label ?? a.value).toLowerCase()
			const hb = String(b.label ?? b.value).toLowerCase()
			return levenshtein(ha, q) - levenshtein(hb, q)
		})

		return [...buckets[0], ...buckets[1], ...buckets[2], ...buckets[3]]
	}

	const filteredOptions = computed(() => {
		// ── API-search mode (default) ─────────────────────────────────────────────
		// When the source is a function and useClientSearch is OFF, the search query
		// is handled server-side; return the fetched list as-is.
		if (hasFetchFn.value && !props.useClientSearch) return internalOptions.value

		// ── Client-search mode ────────────────────────────────────────────────────
		// Pool: for async sources use the already-fetched internalOptions;
		//       for static arrays use props.options directly.
		const pool: SelectOption<T>[] = hasFetchFn.value
			? (internalOptions.value as SelectOption<T>[])
			: (props.options as SelectOption<T>[]) || []

		if (!searchQuery.value) return pool

		return rankOptions(pool, searchQuery.value)
	})

	const selectedOption = computed(() => {
		const options = hasFetchFn.value
			? (internalOptions.value as SelectOption<T>[])
			: (props.options as SelectOption<T>[]) || []
		return options.find(opt => opt.value === modelValue.value)
	})

	onClickOutside(target, event => {
		if (menuRef.value && (menuRef.value === event.target || menuRef.value.contains(event.target as Node))) return
		close()
	})

	onMounted(() => {
		if (hasFetchFn.value && !props.disabled) {
			debouncedFetch()
		}
	})

	watch(
		() => props.disabled,
		isDisabled => {
			if (!isDisabled && hasFetchFn.value && internalOptions.value.length === 0) {
				debouncedFetch()
			}
		}
	)

	watch(
		() => props.watchValue,
		() => {
			if (hasFetchFn.value && !props.disabled) {
				internalOptions.value = []
				debouncedFetch()
			}
		},
		{ deep: true }
	)

	onUnmounted(() => {
		window.removeEventListener('scroll', updatePosition, true)
		window.removeEventListener('resize', updatePosition)
	})
	defineSlots<{
		trigger(props: { selected?: SelectOption<T>; isOpen: boolean }): unknown
		option(props: { option: SelectOption<T>; selected: boolean }): unknown
		footer(): unknown
	}>()
	defineExpose({
		close,
		isOpen
	})
</script>

<script lang="ts">
	export interface UiSelectProps<T = unknown> {
		options?: SelectOption<T>[] | ((query: Record<string, unknown>) => Promise<PaginationContext<unknown[]> | null>)
		optionsFn?: (query: Record<string, unknown>) => Promise<PaginationContext<SelectOption<T>[]> | null>
		labelKey?: string | ((item: Record<string, unknown>) => string)
		valueKey?: string | ((item: Record<string, unknown>) => unknown)
		placeholderClass?: ClassNameValue
		inputClass?: ClassNameValue
		formName?: string | number | undefined
		optionsClass?: ClassNameValue
		placeholder?: string
		disabled?: boolean
		loading?: boolean
		containerClass?: ClassNameValue
		triggerClass?: ClassNameValue
		menuClass?: ClassNameValue
		showArrow?: boolean
		virtual?: boolean
		showSearch?: boolean
		allowClear?: boolean
		watchValue?: unknown
		/**
		 * Controls how the search input filters options.
		 *
		 * - `false` (default) — search is delegated to the API function passed in `options`/`optionsFn`.
		 *   Each keystroke triggers a debounced `fetchOptions` call so the server handles filtering.
		 *
		 * - `true` — client-side search is performed on the already-fetched list (`internalOptions` for
		 *   async sources, or the static `options` array). Results are ranked by relevance:
		 *   1. **Exact match** — label equals the query (case-insensitive)
		 *   2. **Starts-with** — label starts with the query
		 *   3. **Contains** — label contains the query anywhere
		 *   4. **Closest spelling** — remaining items sorted by Levenshtein distance so the closest
		 *      spelling variants surface before alphabetically later options.
		 *
		 * Useful when you already have a complete list from an API call and want instant,
		 * offline filtering without firing additional network requests.
		 */
		useClientSearch?: boolean
	}
</script>

<template>
	<div ref="target" :class="cn('relative', containerClass)">
		<slot name="trigger" :selected="selectedOption" :is-open="isOpen">
			<button
				type="button"
				:disabled="disabled || loading"
				:class="
					cn(
						'flex h-[44px] w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-[6px] py-[6px] text-[10px] text-base font-medium text-gray-600 outline-none transition-all hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-2/20',
						isOpen ? 'border-primary-2 ring-2 ring-primary-2/20' : '',
						disabled ? 'cursor-not-allowed bg-gray-50 opacity-50' : 'cursor-pointer',
						triggerClass
					)
				"
				@click="toggle"
			>
				<span :class="cn('text-gray-700', inputClass)">
					<span
						v-if="modelValue !== null && modelValue !== undefined && String(modelValue).trim() !== ''"
						:id="String(formItemContext.id?.value || formName || '')"
						data-target="value"
						v-html="selectedOption?.label || selectedOption?.value || modelValue"
					/>
					<span v-else :class="cn('text-sm font-normal text-gray-400', placeholderClass)">
						{{ placeholder }}
					</span>
				</span>
				<div class="flex items-center gap-1">
					<tools-icon v-if="isLoading && !isOpen" icon="loading" class="h-4 w-4 animate-spin text-gray-400" />
					<button
						v-if="allowClear && selectedOption"
						type="button"
						class="rounded-full p-0.5 transition-colors hover:bg-gray-100"
						@click.stop="onClear"
					>
						<tools-icon
							icon="cancel"
							dir="arrows"
							:height="16"
							:width="16"
							class="text-gray-400 hover:text-gray-600"
						/>
					</button>
					<tools-icon
						v-if="showArrow"
						icon="caret-down"
						dir="arrows"
						:class="
							cn(
								'flex h-4 w-4 items-center justify-center text-gray-400 transition-transform duration-200',
								isOpen ? 'rotate-180' : ''
							)
						"
					/>
				</div>
			</button>
		</slot>

		<teleport to="body">
			<Transition
				enter-active-class="transition duration-200 ease-out"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition duration-150 ease-in"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<div v-if="isOpen" class="fixed inset-0 z-[1090] bg-black/25 backdrop-blur-[2px]" @click="close" />
			</Transition>
		</teleport>

		<teleport to="body">
			<transition
				enter-active-class="transition duration-200 ease-out"
				:enter-from-class="cn('opacity-0 scale-95', isAbove ? '-translate-y-1' : 'translate-y-1')"
				enter-to-class="opacity-100 translate-y-0 scale-100"
				leave-active-class="transition duration-150 ease-in"
				leave-from-class="opacity-100 translate-y-0 scale-100"
				:leave-to-class="cn('opacity-0 scale-95', isAbove ? '-translate-y-1' : 'translate-y-1')"
			>
				<div
					v-if="isOpen"
					ref="menu"
					:style="menuStyle"
					:class="
						cn(
							'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl shadow-black/5 md:min-w-[200px]',
							menuClass
						)
					"
				>
					<div v-if="showSearch" class="border-b border-gray-200">
						<div class="relative">
							<tools-icon
								icon="search-normal"
								class="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
							/>
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search..."
								class="h-[50px] w-full border-b border-none border-gray-600 bg-gray-50 py-2 pl-4 pr-4 text-xs outline-none active:border-b active:border-primary-2"
								@click.stop
							/>
						</div>
					</div>

					<div
						:class="
							cn(
								'custom-scrollbar max-h-60 min-w-[200px] overflow-y-auto p-1.5',
								virtual ? 'overflow-y-hidden' : ''
							)
						"
						@scroll="handleScroll"
					>
						<ui-virtual-scroll
							v-if="virtual && filteredOptions.length > 50"
							:items="filteredOptions"
							:item-height="40"
							:threshold="50"
							height="240px"
							container-class="w-full"
							@scroll="e => handleScroll(e as unknown as Event)"
						>
							<template #item="{ item: option, style }">
								<div
									v-if="!option.hidden"
									:style
									:class="
										cn(
											'group relative flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors',
											option.disabled
												? 'cursor-not-allowed opacity-50'
												: 'cursor-pointer hover:bg-gray-50',
											modelValue === option.value
												? 'bg-primary-2/5 font-semibold text-primary-2'
												: 'text-gray-700',
											optionsClass,
											option?.class
										)
									"
									@click="selectOption(option)"
								>
									<slot name="option" :option="option" :selected="modelValue === option.value">
										<span class="flex-1 truncate" v-html="option.label || option.value" />
										<tools-icon
											v-if="modelValue === option.value"
											icon="tick-circle"
											dir="fill"
											class="ml-2 h-4 w-4 text-primary-2"
										/>
									</slot>
								</div>
							</template>
						</ui-virtual-scroll>

						<template v-else>
							<template v-for="option in filteredOptions">
								<div
									v-if="!option.hidden"
									:key="String(option.value)"
									:class="
										cn(
											'group relative flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors',
											option.disabled
												? 'cursor-not-allowed opacity-50'
												: 'cursor-pointer hover:bg-gray-50',
											modelValue === option.value
												? 'bg-primary-2/5 font-semibold text-primary-2'
												: 'text-gray-700',
											optionsClass,
											option?.class
										)
									"
									@click="selectOption(option)"
								>
									<slot name="option" :option="option" :selected="modelValue === option.value">
										<span class="flex-1 truncate" v-html="option.label || option.value" />
										<tools-icon
											v-if="modelValue === option.value"
											icon="tick-circle"
											dir="fill"
											class="ml-2 h-4 w-4 text-primary-2"
										/>
									</slot>
								</div>
							</template>

							<div v-if="filteredOptions.length === 0 && !isLoading" class="px-3 py-8 text-center">
								<p class="text-xs text-gray-400">No results found</p>
							</div>
						</template>

						<div v-if="isLoading || isLoadingMore" class="flex items-center justify-center p-4">
							<tools-icon dir="line" icon="eclipse" />
						</div>
					</div>

					<div v-if="$slots.footer" class="border-t border-gray-100 bg-gray-50/50 p-2">
						<slot name="footer" />
					</div>
				</div>
			</transition>
		</teleport>
	</div>
</template>

<style scoped>
	.custom-scrollbar::-webkit-scrollbar {
		width: 6px;
	}
	.custom-scrollbar::-webkit-scrollbar-track {
		background: transparent;
	}
	.custom-scrollbar::-webkit-scrollbar-thumb {
		background: #e2e8f0;
		border-radius: 10px;
	}
	.custom-scrollbar::-webkit-scrollbar-thumb:hover {
		background: #cbd5e1;
	}
</style>
